import { Link, useLocation } from "wouter";
import { useContainers } from "../../api/queries";
import type { Container, PortBinding } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { QueryStates } from "../../components/QueryStates";
import { ContainerStatePill } from "../../components/StatePill";
import "./services.css";

interface ServiceContainersSectionProps {
  serviceId: string;
}

function formatPorts(ports: PortBinding[]): string {
  if (ports.length === 0) return "—";
  return ports
    .map((p) => (p.hostPort ? `${p.hostPort}→${p.containerPort}/${p.protocol}` : `${p.containerPort}/${p.protocol}`))
    .join(", ");
}

const COLUMNS: DataTableColumn<Container>[] = [
  {
    key: "name",
    header: "Container",
    cell: (container) => (
      <Link
        className="data-table__primary-link"
        href={`/containers/${container.nodeId}/${container.id}`}
        // stopPropagation: the row itself is also a click target (DataTable's
        // onRowClick below); without this, activating the link would navigate twice.
        onClick={(e) => e.stopPropagation()}
      >
        {container.name}
      </Link>
    ),
    className: "data-table__primary",
  },
  { key: "state", header: "State", cell: (c) => <ContainerStatePill state={c.state} /> },
  { key: "node", header: "Machine", cell: (c) => c.nodeName },
  { key: "ports", header: "Ports", cell: (c) => formatPorts(c.ports), className: "data-table__mono" },
  { key: "status", header: "Status", cell: (c) => c.status, className: "data-table__secondary" },
];

/**
 * The containers currently realizing this service (#245 gap 2) — the reverse
 * of `ContainerDetailPage`'s Container → Service jump (#288/#395). Each row
 * links to that container's own detail page, where logs/exec/restart already
 * live; this section deliberately does not reimplement those actions, it
 * only makes them reachable from the service an App Developer actually
 * thinks in terms of (charter S-15: no fabricated per-service action that
 * isn't backed by a real container).
 *
 * Fetched via the server-side `?serviceId=` filter (#408) — `useContainers`
 * passes `serviceId` straight through to `listContainers`, so the server
 * resolves each container's owning-service ref (#288) and narrows to this
 * service before the page is even built, replacing the client-side
 * `container.service?.serviceId === serviceId` fan-out this section used
 * to do.
 */
export function ServiceContainersSection({ serviceId }: ServiceContainersSectionProps) {
  const [, navigate] = useLocation();
  const { data, isPending, isError, error, refetch, isFetching } = useContainers({ serviceId, limit: 100 });

  const containers = data?.items ?? [];

  return (
    <section className="service-detail__section" aria-labelledby="service-containers-heading">
      <h2 id="service-containers-heading">Containers</h2>

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading containers…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error ? error.message : "Couldn't load containers. Check your connection and try again."
        }
        isEmpty={containers.length === 0}
        empty={
          <EmptyState
            title="No running containers for this service yet"
            description="Once this service is running on a machine, its containers show up here with links to their logs, terminal, and restart controls."
          />
        }
      >
        <DataTable
          columns={COLUMNS}
          rows={containers}
          rowKey={(container) => container.id}
          onRowClick={(container) => navigate(`/containers/${container.nodeId}/${container.id}`)}
          ariaBusy={isFetching}
        />
      </QueryStates>
    </section>
  );
}
